import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSite } from '../context/SiteContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import TripCard from '../components/TripCard';
import { IconArrow, IconMapPin } from '../components/Icons';
import styles from './AllTrips.module.css';

const DISTANCE_FILTERS = [
  { label: 'All Distances', value: 'all' },
  { label: 'Under 3 KM', value: 'under3' },
  { label: '3 – 5 KM', value: '3to5' },
  { label: '5+ KM', value: 'over5' },
];

function parseKm(distance) {
  const n = parseInt(distance);
  return isNaN(n) ? null : n;
}

function matchesDistance(trip, filter) {
  if (filter === 'all') return true;
  const km = parseKm(trip.distance);
  if (km === null) return false;
  if (filter === 'under3') return km < 3;
  if (filter === '3to5') return km >= 3 && km <= 5;
  if (filter === 'over5') return km > 5;
  return true;
}

export default function AllTrips() {
  const { data } = useSite();
  const navigate = useNavigate();

  const [category, setCategory] = useState('all');
  const [distance, setDistance] = useState('all');
  const [location, setLocation] = useState('all');

  const activeTrips = data.trips.filter(t => !t.softDeleted);

  const locations = ['all', ...Array.from(new Set(activeTrips.map(t => t.location))).sort()];

  const filtered = activeTrips.filter(t =>
    (category === 'all' || t.category === category) &&
    matchesDistance(t, distance) &&
    (location === 'all' || t.location === location)
  );

  const hasFilters = category !== 'all' || distance !== 'all' || location !== 'all';

  return (
    <>
      <SEO
        title="Hiking Trips in Jordan | Explore All Adventures"
        description="Discover our complete collection of hiking trips across Jordan. Browse by distance, difficulty level, and location. From moderate walks to challenging hikes."
        image="https://keepersexperience.com/full_logo.png"
        url="https://keepersexperience.com/trips"
        type="website"
      />
      <div className={styles.page}>
      <Navbar />
      <div className={styles.hero}>
        <button className={styles.backBtn} onClick={() => navigate('/')}>
          <span className={styles.backArrow}><IconArrow size={14} style={{ transform: 'rotate(180deg)' }} /></span>
          Back to home
        </button>
        <span className={styles.heroLabel}>All Adventures</span>
        <h1 className={styles.heroTitle}>All Trips</h1>
        <p className={styles.heroSub}>{activeTrips.length} trips across Jordan's most spectacular landscapes</p>
      </div>

      <div className={styles.container}>
        {/* ─── FILTERS ─── */}
        <div className={styles.filtersBar}>
          {/* Category */}
          <div className={styles.filterGroup}>
            <span className={styles.filterLabel}>Type</span>
            <div className={styles.filterChips}>
              <button
                key="all"
                className={`${styles.chip} ${category === 'all' ? styles.chipActive : ''}`}
                onClick={() => setCategory('all')}
              >All</button>
              {(data.categories || []).map(cat => (
                <button
                  key={cat.id}
                  className={`${styles.chip} ${category === cat.id ? styles.chipActive : ''}`}
                  onClick={() => setCategory(cat.id)}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Distance */}
          <div className={styles.filterGroup}>
            <span className={styles.filterLabel}>Distance</span>
            <div className={styles.filterChips}>
              {DISTANCE_FILTERS.map(d => (
                <button
                  key={d.value}
                  className={`${styles.chip} ${distance === d.value ? styles.chipActive : ''}`}
                  onClick={() => setDistance(d.value)}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          {/* Location */}
          <div className={styles.filterGroup}>
            <span className={styles.filterLabel}><IconMapPin size={12} /> Location</span>
            <select
              className={styles.locationSelect}
              value={location}
              onChange={e => setLocation(e.target.value)}
            >
              {locations.map(loc => (
                <option key={loc} value={loc}>
                  {loc === 'all' ? 'All Locations' : loc}
                </option>
              ))}
            </select>
          </div>

          {hasFilters && (
            <button
              className={styles.clearBtn}
              onClick={() => { setCategory('all'); setDistance('all'); setLocation('all'); }}
            >
              Clear filters
            </button>
          )}
        </div>

        {/* ─── RESULTS COUNT ─── */}
        <div className={styles.resultsRow}>
          <span className={styles.resultsCount}>
            {filtered.length} trip{filtered.length !== 1 ? 's' : ''} found
          </span>
        </div>

        {/* ─── GRID ─── */}
        {filtered.length > 0 ? (
          <div className={styles.grid}>
            {filtered.map((trip, i) => (
              <div key={trip.id} className={styles.cardWrap} style={{ animationDelay: `${i * 0.06}s` }}>
                <TripCard trip={trip} />
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.empty}>
            <div className={styles.emptyIcon}>🌿</div>
            <p>No trips match your filters.</p>
            <button className={styles.clearBtn} onClick={() => { setCategory('all'); setDistance('all'); setLocation('all'); }}>
              Clear all filters
            </button>
          </div>
        )}
      </div>
      <Footer />
    </div>
    </>
  );
}
